/**
 * Synthesized daily usage series for the dashboard area chart.
 *
 * @remarks
 * The /v1/status endpoint exposes `generationsThisCycle` as a single scalar.
 * Until a per-day history endpoint ships (tracked in Epic 11 follow-up),
 * we generate a plausible 14-day distribution whose total equals the known
 * cycle total. The generator is deterministic (seeded on inputs) so the
 * chart does not flicker between polls.
 *
 * TODO: replace with `/v1/usage/history` when available. The shape returned
 * here (`UsageSeriesPoint[]`) is the same we'd consume from the real API.
 */

export interface UsageSeriesPoint {
  /** ISO date (YYYY-MM-DD) for the data point. */
  date: string;
  /** Token / generation count for that day. */
  value: number;
}

const SERIES_LENGTH = 14;

/**
 * Deterministic PRNG — Mulberry32. Small, good-enough distribution, and
 * most importantly avoids the chart "dancing" on re-renders / polls.
 */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i += 1) {
    h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Build a 14-day cycle series that sums to `total` generations. The starting
 * day is derived from `cycleResetDate` (a future reset date — we walk back
 * 14 days so the latest point is "today"-ish relative to the cycle).
 */
export function buildUsageSeries(
  total: number,
  cycleResetDate: string | null,
  length: number = SERIES_LENGTH,
): UsageSeriesPoint[] {
  const safeTotal = Math.max(0, Math.floor(total));
  const reset = cycleResetDate ? new Date(cycleResetDate) : new Date();
  const startIso = addDays(reset.toISOString().slice(0, 10), -length);

  // Build weights via a smooth random walk, then normalize.
  const seed = hashString(`${startIso}:${safeTotal}`);
  const rand = mulberry32(seed);
  const weights: number[] = new Array(length);
  let level = 0.5;
  for (let i = 0; i < length; i += 1) {
    const delta = (rand() - 0.5) * 0.4;
    level = Math.max(0.05, Math.min(1, level + delta));
    weights[i] = level;
  }

  if (safeTotal === 0) {
    return weights.map((_, i) => ({ date: addDays(startIso, i), value: 0 }));
  }

  const sum = weights.reduce((a, b) => a + b, 0);
  const raw = weights.map((w) => (w / sum) * safeTotal);

  // Integer rounding with largest-remainder correction so the series totals
  // exactly `safeTotal`.
  const floored = raw.map((v) => Math.floor(v));
  let remainder = safeTotal - floored.reduce((a, b) => a + b, 0);
  const fracs = raw.map((v, i) => ({ i, frac: v - Math.floor(v) })).sort((a, b) => b.frac - a.frac);
  for (const { i } of fracs) {
    if (remainder <= 0) break;
    floored[i] = (floored[i] ?? 0) + 1;
    remainder -= 1;
  }

  return floored.map((value, i) => ({ date: addDays(startIso, i), value }));
}
